<template>
  <b-modal :id="modalId" :visible="show" @hidden="$emit('close')" title="Tải dữ liệu từ Google Sheet/CSV" size="xl" hide-footer>
    <!-- Simplified to online scan only -->
    <div class="mb-3">
      <h5>Tải dữ liệu Quét online</h5>
    </div>

    <b-tabs v-model="activeTab" content-class="mt-3">
      <b-tab title="Tải file CSV/XLSX">
        <b-form-file v-model="selectedFile" accept=".csv,.tsv,.txt" @change="handleFileChange" browse-text="Chọn file" placeholder="Chưa chọn file" />
      </b-tab>
      <b-tab title="Dán trực tiếp từ Google Sheet">
        <b-form-textarea v-model="pastedText" rows="8" placeholder="Dán nội dung đã copy từ Google Sheet (dạng bảng)" />
        <div class="mt-2 text-muted small">Gợi ý: Chọn toàn bộ dữ liệu trong Sheet và dán vào đây. Hệ thống tự phát hiện cột theo dòng đầu tiên.</div>
      </b-tab>
    </b-tabs>

    <div class="mt-3 d-flex">
      <b-button variant="primary" class="mr-2" :disabled="isParsing" @click="parseInput">Phân tích dữ liệu</b-button>
      <b-button variant="secondary" class="mr-2" :disabled="!hasParsed" @click="resetAll">Làm lại</b-button>
      <b-button variant="outline-secondary" class="ml-auto" @click="$emit('close')">Đóng</b-button>
    </div>

    <b-alert show variant="info" class="mt-3" v-if="isParsing">Đang phân tích dữ liệu...</b-alert>

    <div v-if="hasParsed" class="mt-4">
      <!-- Mapping + bulk preview for FILE mode only -->
      <template v-if="!reviewMode">
        <h5>Ghép cột</h5>
        <div class="mapping-grid">
          <div class="mapping-row" v-for="col in internalColumns" :key="col">
            <div class="mapping-col-name">{{ col }}</div>
            <div class="mapping-col-select">
              <b-form-select v-model="columnMapping[col]" :options="headerOptions" />
            </div>
          </div>
        </div>

        <div class="mt-3 d-flex align-items-center">
          <b-button variant="primary" class="mr-2" :disabled="isDeduping" @click="applyMappingAndPreview">Xem trước & đánh dấu trùng</b-button>
          <b-form-checkbox v-model="excludeDuplicates" switch class="ml-2">Bỏ qua bản trùng (khuyến nghị)</b-form-checkbox>
          <b-button variant="link" class="ml-2 p-0" @click="isDryRun = !isDryRun">{{ isDryRun ? 'Tắt Dry-run' : 'Bật Dry-run' }}</b-button>
        </div>

        <b-alert show variant="info" class="mt-3" v-if="isDeduping">Đang kiểm tra trùng lặp...</b-alert>

        <div v-if="previewRows.length" class="mt-3">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <div>
              <b-badge variant="success" class="mr-2">Mới: {{ uniqueCount }}</b-badge>
              <b-badge variant="warning">Trùng: {{ duplicateCount }}</b-badge>
            </div>
            <div>
              <b-button size="sm" variant="success" :disabled="isImporting || previewRows.length===0" @click="performImport">{{ isDryRun ? 'Giả lập nhập' : 'Nhập dữ liệu' }}</b-button>
            </div>
          </div>

          <div class="table-responsive" style="max-height: 360px; overflow:auto;">
            <table class="table table-sm table-bordered">
              <thead class="thead-dark">
                <tr>
                  <th>Nhập</th>
                  <th>Trạng thái</th>
                  <th v-for="h in previewHeaders" :key="h">{{ h }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in previewRows" :key="idx" :class="row.isDuplicate ? 'table-warning' : ''">
                  <td>
                    <b-form-checkbox v-model="row.include" :disabled="row.isDuplicate && excludeDuplicates" />
                  </td>
                  <td>
                    <span v-if="row.isDuplicate" title="Bản ghi có thể đã tồn tại">Trùng</span>
                    <span v-else>Mới</span>
                  </td>
                  <td v-for="h in previewHeaders" :key="h">{{ row[h] }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="importSummary" class="mt-3">
          <b-alert show :variant="isDryRun ? 'info' : 'success'">
            {{ isDryRun ? 'Kết quả giả lập' : 'Kết quả nhập' }} — Thêm mới: {{ importSummary.inserted }}, Bỏ qua: {{ importSummary.skipped }}, Lỗi: {{ importSummary.errors.length }}
          </b-alert>
          <div v-if="importSummary.errors.length">
            <b-button size="sm" variant="outline-danger" @click="downloadErrors">Tải danh sách lỗi</b-button>
          </div>
        </div>
      </template>

      <!-- Record-by-record review for PASTE mode -->
      <template v-else>
        <div class="d-flex justify-content-between align-items-center mb-3">
          <div>
            <b-badge variant="dark">Bản ghi {{ currentIndex + 1 }} / {{ previewRows.length }}</b-badge>
            <b-badge :variant="currentRecord.isDuplicate ? 'warning' : 'success'" class="ml-2">
              {{ currentRecord.isDuplicate ? 'Trùng' : 'Mới' }}
            </b-badge>
          </div>
          <div>
            <b-button size="sm" class="mr-2" :disabled="currentIndex===0" @click="prevRecord">Trước</b-button>
            <b-button size="sm" :disabled="currentIndex>=previewRows.length-1" @click="nextRecord">Sau</b-button>
          </div>
        </div>

        <b-alert v-if="checkingDuplicate" show variant="info">Đang kiểm tra trùng...</b-alert>
        <b-alert v-else-if="currentRecord.isDuplicate" show variant="warning">Bản ghi có thể đã tồn tại. Bạn có thể chỉnh sửa hoặc bỏ qua.</b-alert>

        <!-- Temporarily simplified form for debugging -->
        <div v-if="reviewForms.length > 0" class="simple-form">
          <div class="row">
            <div class="col-md-6" v-for="field in reviewFields.slice(0, 10)" :key="field.col">
              <b-form-group :label="field.label">
                <b-form-input v-if="!field.type || field.type==='text'" v-model="currentRecord[field.col]" />
                <b-form-input v-else-if="field.type==='date'" type="date" v-model="currentRecord[field.col]" />
                <b-form-input v-else-if="field.type==='number'" type="number" step="0.001" v-model.number="currentRecord[field.col]" />
                <b-form-textarea v-else-if="field.type==='textarea'" rows="3" v-model="currentRecord[field.col]" />
                <b-form-checkbox v-else-if="field.type==='checkbox'" v-model="currentRecord[field.col]" :value="1" :unchecked-value="0">{{ field.label }}</b-form-checkbox>
                <b-form-select v-else-if="hasEnum(field.col)" :options="getEnumOptions(field.col)" v-model="currentRecord[field.col]" />
                <b-form-input v-else v-model="currentRecord[field.col]" />
              </b-form-group>
            </div>
          </div>
        </div>
        <div v-else class="text-center p-4">
          <p>Đang tải form...</p>
        </div>

        <div class="d-flex justify-content-between align-items-center mt-3">
          <div>
            <b-button variant="secondary" :disabled="currentIndex===0" @click="prevRecord">Trước</b-button>
          </div>
          <div>
            <b-button class="mr-2" variant="outline-primary" @click="skipRecord">Bỏ qua</b-button>
            <b-button variant="primary" :disabled="isImporting" @click="submitCurrentSimple">Gửi bản ghi này</b-button>
          </div>
        </div>
      </template>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios'
import moment from 'moment'
import Constants from '@/constants'
import WardData from '@/models/WardData'
import DirectionData from '@/models/DirectionData'
import StructureData from '@/models/StructureData'
import LineTypeData from '@/models/LineTypeData'
import LandUsageTypeData from '@/models/LandUsageTypeData'
import TypeData from '@/models/TypeData'
import StreetData from '@/models/StreetData'
import OnlineScanTableMeta from '@/models/OnlineScanTableMeta'

export default {
  name: 'UploadDialog',
  components: {
    // Lazy import to avoid circular deps if needed
    'generic-form-view': () => import('@/views/base/GenericFormView')
  },
  props: {
    show: { type: Boolean, default: false },
    modalId: { type: String, default: 'upload-dialog' }
  },
  data () {
    return {
      localTarget: 'online_scan', // Fixed to online scan only
      activeTab: 0,
      selectedFile: null,
      pastedText: '',
      isParsing: false,
      hasParsed: false,
      rawHeaders: [],
      rawRows: [],
      columnMapping: {},
      isDeduping: false,
      previewRows: [],
      previewHeaders: [],
      excludeDuplicates: true,
      isDryRun: true,
      isImporting: false,
      importSummary: null,
      reviewMode: false,
      currentIndex: 0,
      checkingDuplicate: false,
      // GenericFormView driven
      reviewForms: [],
      activeColumns: {},
      activeTableName: '',
      rootPath: ''
    }
  },
  computed: {
    headerOptions () {
      return [ { value: null, text: '— Bỏ qua —' }, ...this.rawHeaders.map(h => ({ value: h, text: h })) ]
    },
    internalColumns () {
      return ['date','fullname','type','ward','street','lineType','address','area','areaWidth','areaLength','quantity','usageType','houseUsageArea','direction','structure','rent','price','pricePerArea','trait','phone','positive']
    },
    uniqueCount () {
      return this.previewRows.filter(r => r.include && !r.isDuplicate).length
    },
    duplicateCount () {
      return this.previewRows.filter(r => r.isDuplicate).length
    },
    currentRecord () {
      return this.previewRows[this.currentIndex] || {}
    },
    reviewFields () {
      // Online scan fields only
      return [
        { col: 'date', label: 'Ngày', type: 'date' },
        { col: 'fullname', label: 'Họ tên' },
        { col: 'type', label: 'Người rao' },
        { col: 'phone', label: 'SĐT' },
        { col: 'ward', label: 'Phường' },
        { col: 'street', label: 'Tên đường' },
        { col: 'address', label: 'Địa chỉ', type: 'textarea' },
        { col: 'lineType', label: 'Loại đường' },
        { col: 'area', label: 'DT đất', type: 'number' },
        { col: 'areaWidth', label: 'Ngang', type: 'number' },
        { col: 'areaLength', label: 'Dài', type: 'number' },
        { col: 'quantity', label: 'Số lô', type: 'number' },
        { col: 'usageType', label: 'Loại đất' },
        { col: 'houseUsageArea', label: 'DT sàn', type: 'number' },
        { col: 'direction', label: 'Hướng' },
        { col: 'structure', label: 'Kết cấu' },
        { col: 'rent', label: 'Cho thuê', type: 'checkbox' },
        { col: 'price', label: 'Giá', type: 'number' },
        { col: 'pricePerArea', label: 'Giá/m2', type: 'number' },
        { col: 'trait', label: 'Đặc điểm', type: 'textarea' },
        { col: 'positive', label: 'Ghi chú', type: 'textarea' }
      ]
    },
    enumOptionsMap () {
      return {
        ward: WardData.data,
        direction: DirectionData.data,
        structure: StructureData.data,
        lineType: LineTypeData.data,
        usageType: LandUsageTypeData.data,
        type: (TypeData && TypeData.data) ? TypeData.data : []
      }
    }
  },
  watch: {
    // No longer needed since we're fixed to online scan
  },
  methods: {
    rebuildReviewForms () {
      try {
        console.log('rebuildReviewForms called')
        const meta = OnlineScanTableMeta
        if (!meta || !meta.columns || !meta.input_view) {
          throw new Error('OnlineScanTableMeta is not properly defined')
        }
        console.log('OnlineScanTableMeta loaded:', meta.table_name)
        
        this.activeColumns = meta.columns
        this.activeTableName = meta.table_name
        this.rootPath = '/v2/online_scan'
        const forms = JSON.parse(JSON.stringify(meta.input_view.forms || []))
        const rec = { ...this.currentRecord }
        
        console.log('Current record:', rec)
        console.log('Forms to populate:', forms.length)
        
        // Pre-populate form values from current record
        forms.forEach(f => {
          const col = f.col
          let val = rec[col]
          if (val === undefined || val === null) {
            f.value = typeof f.defaultValue === 'function' ? f.defaultValue() : (f.defaultValue || null)
            return
          }
          if (f.type === 'multi-select') {
            if (Array.isArray(val)) f.value = val
            else if (typeof val === 'string') f.value = val.split(',').map(s => s.trim()).filter(Boolean)
            else f.value = []
          } else if (f.type === 'checkbox') {
            f.value = !!(val === true || val === 1 || val === '1' || val === 'true')
          } else if (f.type === 'date') {
            f.value = val
          } else {
            f.value = val
          }
        })
        this.reviewForms = forms
        console.log('Review forms set:', this.reviewForms.length)
        
        // Simplified - no GenericFormView for now
        console.log('Forms built successfully')
      } catch (error) {
        console.error('rebuildReviewForms error:', error)
        alert('Lỗi khi xây dựng form: ' + error.message)
      }
    },
    syncCurrentRecordFromForms () {
      if (!this.$refs.genericForm || !this.$refs.genericForm.forms) return
      const updated = { ...this.currentRecord }
      this.$refs.genericForm.forms.forEach(f => {
        if (f.type === 'multi-select') {
          updated[f.col] = Array.isArray(f.value) ? f.value.join(',') : (f.value || '')
        } else if (f.type === 'checkbox') {
          updated[f.col] = f.value ? 1 : 0
        } else {
          updated[f.col] = f.value
        }
      })
      Object.assign(this.currentRecord, updated)
    },
    hasEnum (col) {
      return !!this.enumOptionsMap[col]
    },
    getEnumOptions (col) {
      const opts = this.enumOptionsMap[col] || []
      return opts.map(o => ({ value: o, text: o }))
    },
    resetAll () {
      this.selectedFile = null
      this.pastedText = ''
      this.isParsing = false
      this.hasParsed = false
      this.rawHeaders = []
      this.rawRows = []
      this.columnMapping = {}
      this.isDeduping = false
      this.previewRows = []
      this.previewHeaders = []
      this.excludeDuplicates = true
      this.isDryRun = true
      this.isImporting = false
      this.importSummary = null
    },
    handleFileChange (evt) {
      const file = evt && evt.target ? evt.target.files[0] : this.selectedFile
      if (!file) return
      const reader = new FileReader()
      reader.onload = e => {
        this.pastedText = e.target.result
      }
      reader.readAsText(file, 'utf-8')
    },
    parseInput () {
      this.isParsing = true
      setTimeout(() => {
        try {
          const text = (this.pastedText || '').trim()
          if (!text) {
            this.isParsing = false
            this.hasParsed = false
            return
          }
          console.log('Parsing text:', text.substring(0, 200) + '...')
          const rows = this.naiveParse(text)
          if (!rows || rows.length === 0) {
            this.isParsing = false
            this.hasParsed = false
            return
          }
          console.log('Parsed rows:', rows.length, 'headers:', rows[0])
          this.rawHeaders = rows[0]
          this.rawRows = rows.slice(1).map(r => this.headersToObject(this.rawHeaders, r))
          this.initDefaultMapping()
          // If user is in Paste tab, switch to review mode automatically after dedup
          this.hasParsed = true
          this.isParsing = false
          if (this.activeTab === 1) {
            console.log('Entering paste mode review')
            // Auto-generate preview with mapping and go to record review
            this.previewHeaders = this.internalColumns
            const mapped = this.rawRows.map(row => {
              const obj = {}
              this.internalColumns.forEach(col => {
                const header = this.columnMapping[col]
                obj[col] = header ? row[header] : ''
              })
              return this.normalizeRow(obj)
            })
            this.previewRows = mapped.map(r => ({ ...r, include: true, isDuplicate: false }))
            this.reviewMode = true
            this.currentIndex = 0
            console.log('About to rebuild forms')
            this.$nextTick(() => {
              this.rebuildReviewForms()
              console.log('Forms rebuilt, starting dedup')
              this.performDedup().then(() => {
                console.log('Dedup complete, checking current duplicate')
                this.checkCurrentDuplicate()
              }).catch(e => {
                console.error('Dedup error:', e)
                this.isDeduping = false
              })
            })
            return
          }
          this.isParsing = false
        } catch (error) {
          console.error('Parse error:', error)
          this.isParsing = false
          this.hasParsed = false
          alert('Lỗi khi phân tích dữ liệu: ' + error.message)
        }
      }, 10)
    },
    naiveParse (text) {
      // Prefer TSV (Google Sheet copy) else fallback to CSV split
      const lines = text.split(/\r?\n/).filter(l => l.trim() !== '')
      if (lines.length === 0) return []
      const isTSV = lines[0].includes('\t')
      if (isTSV) {
        return lines.map(l => l.split('\t'))
      }
      // Basic CSV split (no nested quotes support)
      return lines.map(l => l.split(','))
    },
    headersToObject (headers, values) {
      const obj = {}
      headers.forEach((h, i) => {
        obj[h] = values[i] !== undefined ? values[i] : ''
      })
      return obj
    },
    initDefaultMapping () {
      const dict = this.defaultHeaderMapOnline()
      this.columnMapping = {}
      this.internalColumns.forEach(col => {
        const matchedHeader = Object.keys(dict).find(h => dict[h] === col)
        if (matchedHeader && this.rawHeaders.includes(matchedHeader)) {
          this.columnMapping[col] = matchedHeader
        } else {
          this.columnMapping[col] = null
        }
      })
    },
    defaultHeaderMapOnline () {
      return {
        'Ngày tháng': 'date',
        'Họ tên': 'fullname',
        'Người rao bán': 'type',
        'Phường': 'ward',
        'Tên đường': 'street',
        'Loại đường': 'lineType',
        'Địa chỉ': 'address',
        'Diện tích đất (m2)': 'area',
        'Đất ngang (m)': 'areaWidth',
        'Đất dài (m)': 'areaLength',
        'Số lô': 'quantity',
        'Loại đất': 'usageType',
        'Diện tích sàn (m2)': 'houseUsageArea',
        'Hướng': 'direction',
        'Kết cấu': 'structure',
        'Cho thuê': 'rent',
        'Giá': 'price',
        'Giá theo m2': 'pricePerArea',
        'Đặc điểm': 'trait',
        'Số điện thoại': 'phone',
        'Ghi chú chung': 'positive'
      }
    },

    applyMappingAndPreview: async function () {
      // Build preview rows with normalized values and run dedup
      this.previewHeaders = this.internalColumns
      const mapped = this.rawRows.map(row => {
        const obj = {}
        this.internalColumns.forEach(col => {
          const header = this.columnMapping[col]
          obj[col] = header ? row[header] : ''
        })
        return this.normalizeRow(obj)
      })
      this.previewRows = mapped.map(r => ({ ...r, include: true, isDuplicate: false }))
      await this.performDedup()
    },
    normalizeRow (obj) {
      const norm = { ...obj }
      // Date
      if (norm.date) {
        norm.date = this.normalizeDate(norm.date)
      }
      // Numbers
      const numberFields = ['area','areaWidth','areaLength','quantity','houseUsageArea','price','pricePerArea']
      numberFields.forEach(f => { if (norm[f] !== '' && norm[f] !== null && norm[f] !== undefined) norm[f] = this.normalizeNumber(norm[f]) })
      // Boolean for rent (online scan only)
      norm.rent = this.normalizeBoolean(norm.rent) ? 1 : 0
      // Map to defined enums (case-insensitive fuzzy match)
      norm.ward = this.mapToEnum(norm.ward, WardData.data)
      norm.direction = this.mapToEnum(norm.direction, DirectionData.data)
      norm.structure = this.mapToEnum(norm.structure, StructureData.data)
      norm.lineType = this.mapToEnum(norm.lineType, LineTypeData.data)
      norm.usageType = this.mapToEnum(norm.usageType, LandUsageTypeData.data)
      // Derive street_code from ward + street if possible
      norm.street_code = this.findStreetCode(norm.ward, norm.street)

      // Defaults
      // if (!norm.type) norm.type = 'Môi giới'
      // if (!norm.lineType) norm.lineType = 'Mặt tiền'
      // if (!norm.structure) norm.structure = 'Đất trống'
      // if (!norm.usageType) norm.usageType = 'ODT'

      // No product code needed for online scan
      // Helper fields for dedup
      norm.address_lc = (norm.address || '').toString().trim().toLowerCase()
      
      return norm
    },
    normalizeDate (val) {
      const s = (val || '').toString().trim()
      // try dd/MM/yyyy
      const m1 = moment(s, 'DD/MM/YYYY', true)
      if (m1.isValid()) return m1.format('YYYY-MM-DD')
      // try yyyy-MM-dd
      const m2 = moment(s, 'YYYY-MM-DD', true)
      if (m2.isValid()) return m2.format('YYYY-MM-DD')
      // try auto
      const ma = moment(s)
      if (ma.isValid()) return ma.format('YYYY-MM-DD')
      return ''
    },
    normalizeNumber (val) {
      const s = (val || '').toString().replace(/\./g, '').replace(/\s/g, '').replace(/,/g, '.')
      const n = Number(s)
      return isNaN(n) ? null : n
    },
    normalizeBoolean (val) {
      const s = (val || '').toString().trim().toLowerCase()
      return s === '1' || s === 'true' || s === 'x' || s === 'yes' || s === 'có'
    },
    mapToEnum (value, options) {
      if (!value) return ''
      const v = value.toString().trim()
      if (!v) return ''
      // exact match first
      const exact = options.find(o => o === v)
      if (exact) return exact
      // case-insensitive exact
      const lower = v.toLowerCase()
      const ci = options.find(o => (o || '').toString().toLowerCase() === lower)
      if (ci) return ci
      // contains match (e.g., stray spacing or suffixes)
      const contains = options.find(o => lower.includes((o || '').toString().toLowerCase()))
      if (contains) return contains
      // fallback: return original value (will be stored as-is)
      return v
    },
    findStreetCode (ward, street) {
      try {
        const wardLc = (ward || '').toString().trim().toLowerCase()
        const streetLc = (street || '').toString().trim().toLowerCase()
        if (!streetLc) return ''
        // Prefer same ward
        let candidates = (StreetData && StreetData.street) ? StreetData.street : []
        let filtered = candidates.filter(s => (s.ward || '').toString().toLowerCase() === wardLc)
        const withinWard = filtered.length > 0 ? filtered : candidates
        // Exact/contains match on street name or street_2
        const exact = withinWard.find(s => (s.street || '').toString().toLowerCase() === streetLc || (s.street_2 || '').toString().toLowerCase() === streetLc)
        if (exact) return exact.code
        const contains = withinWard.find(s => streetLc.includes((s.street || '').toString().toLowerCase()) || streetLc.includes((s.street_2 || '').toString().toLowerCase()))
        if (contains) return contains.code
        return ''
      } catch (e) {
        return ''
      }
    },
    normalizePhone (val) {
      const digits = (val || '').toString().replace(/\D/g, '')
      if (!digits) return ''
      if (digits.length <= 10) return digits
      return digits.slice(-10)
    },
    async performDedup () {
      console.log('performDedup started')
      this.isDeduping = true
      try {
        const phones = Array.from(new Set(this.previewRows.map(r => r.normalized_phone).filter(Boolean)))
        const chunks = this.chunkArray(phones, 50)
        let existing = []
        for (const chunk of chunks) {
          const inList = chunk.map(p => `'${p}'`).join(',')
          const table = 'realestate.online_scan'
          const selectCols = 'id, LOWER(address) as address_lc, area, phone, date'
          const query = `SELECT ${selectCols} FROM ${table} WHERE phone IN (${inList})`
          const url = Constants.HOST_URL + 'runSelectQuery?query=' + encodeURIComponent(query)
          const resp = await axios.get(url)
          existing = existing.concat(resp.data || [])
        }
        // Fallback for rows without phone: query by address
        const addrSet = new Set(
          this.previewRows
            .filter(r => !r.normalized_phone && r.address_lc)
            .map(r => r.address_lc)
        )
        const addresses = Array.from(addrSet)
        const addrChunks = this.chunkArray(addresses, 30)
        for (const chunk of addrChunks) {
          const addrIn = chunk.map(a => `'${this.escapeSql(a)}'`).join(',')
          const table = 'realestate.online_scan'
          const selectCols = 'id, LOWER(address) as address_lc, area, phone, date'
          const query = `SELECT ${selectCols} FROM ${table} WHERE LOWER(address) IN (${addrIn})`
          const url = Constants.HOST_URL + 'runSelectQuery?query=' + encodeURIComponent(query)
          const resp = await axios.get(url)
          existing = existing.concat(resp.data || [])
        }
        // mark duplicates
        this.previewRows.forEach(r => {
          const match = existing.find(e => {
            if (!e.address_lc) return false
            const addrMatch = (e.address_lc || '').trim() === (r.address_lc || '').trim()
            const areaMatch = e.area != null && r.area != null ? Math.abs(Number(e.area) - Number(r.area)) <= 1 : false
            const withinDays = e.date ? Math.abs(moment(e.date).diff(moment(r.date), 'days')) <= 45 : true
            const phoneOk = r.normalized_phone && r.normalized_phone === this.normalizePhone(e.phone)
            return ((phoneOk && addrMatch && areaMatch) || (addrMatch && areaMatch)) && withinDays
          })
          r.isDuplicate = !!match
          if (this.excludeDuplicates && r.isDuplicate) r.include = false
        })
      } catch (e) {
        console.error('performDedup error:', e)
        alert('Lỗi khi kiểm tra trùng: ' + e)
      } finally {
        console.log('performDedup completed')
        this.isDeduping = false
      }
    },
    async checkCurrentDuplicate () {
      // Ensure current record has up-to-date duplicate status
      if (!this.previewRows.length) return
      this.checkingDuplicate = true
      try {
        const r = this.currentRecord
        // lightweight query by phone or address
        const table = 'realestate.online_scan'
        let query = ''
        if (r.normalized_phone) {
          query = `SELECT id, LOWER(address) as address_lc, area, phone, date FROM ${table} WHERE phone='${r.normalized_phone}'`
        } else if (r.address_lc) {
          query = `SELECT id, LOWER(address) as address_lc, area, phone, date FROM ${table} WHERE LOWER(address)='${this.escapeSql(r.address_lc)}'`
        }
        if (query) {
          const url = Constants.HOST_URL + 'runSelectQuery?query=' + encodeURIComponent(query)
          const resp = await axios.get(url)
          const exist = (resp.data || []).find(e => {
            const addrMatch = (e.address_lc || '').trim() === (r.address_lc || '').trim()
            const areaMatch = e.area != null && r.area != null ? Math.abs(Number(e.area) - Number(r.area)) <= 1 : false
            const withinDays = e.date ? Math.abs(moment(e.date).diff(moment(r.date), 'days')) <= 45 : true
            const phoneOk = r.normalized_phone && r.normalized_phone === this.normalizePhone(e.phone)
            return ((phoneOk && addrMatch && areaMatch) || (addrMatch && areaMatch)) && withinDays
          })
          this.$set(this.previewRows[this.currentIndex], 'isDuplicate', !!exist)
          if (this.excludeDuplicates && this.previewRows[this.currentIndex].isDuplicate) {
            this.$set(this.previewRows[this.currentIndex], 'include', false)
          }
        }
      } catch (e) {
        // swallow
      } finally {
        this.checkingDuplicate = false
      }
    },
    prevRecord () {
      if (this.currentIndex > 0) {
        this.currentIndex -= 1
        this.rebuildReviewForms()
        this.checkCurrentDuplicate()
      }
    },
    nextRecord () {
      if (this.currentIndex < this.previewRows.length - 1) {
        this.currentIndex += 1
        this.rebuildReviewForms()
        this.checkCurrentDuplicate()
      }
    },
    skipRecord () {
      this.$set(this.previewRows[this.currentIndex], 'include', false)
      this.nextRecord()
    },
    async triggerGenericSubmit () {
      // Sync data from GenericFormView back to current record first
      this.syncCurrentRecordFromForms()
      
      // check duplicate again then insert this single record
      await this.checkCurrentDuplicate()
      const r = this.currentRecord
      if (r.isDuplicate) {
        this.$bvToast && this.$bvToast.toast('Bản ghi bị trùng, đã bỏ qua', { title: 'Upload', variant: 'warning', autoHideDelay: 1500 })
        this.skipRecord()
        return
      }
      
      // Delegate to GenericFormView submit if available, otherwise fallback to direct insert
      if (this.$refs.genericForm && this.$refs.genericForm.submit) {
        this.$refs.genericForm.submit()
      } else {
        // Fallback: Direct insert for online scan
        try {
          const { query, url } = this.buildInsertQuery(r)
          const resp = await axios.post(url, { query })
          if (!resp.data) throw new Error('No response')
          this.$bvToast && this.$bvToast.toast('Đã thêm bản ghi', { title: 'Upload', variant: 'success', autoHideDelay: 1500 })
          this.$set(this.previewRows[this.currentIndex], 'include', false)
          this.nextRecord()
        } catch (e) {
          alert('Lỗi khi thêm bản ghi: ' + e)
        }
      }
    },
    async submitCurrentSimple () {
      // Simplified submit without GenericFormView
      console.log('submitCurrentSimple called')
      await this.checkCurrentDuplicate()
      const r = this.currentRecord
      if (r.isDuplicate) {
        this.$bvToast && this.$bvToast.toast('Bản ghi bị trùng, đã bỏ qua', { title: 'Upload', variant: 'warning', autoHideDelay: 1500 })
        this.skipRecord()
        return
      }
      
      try {
        const { query, url } = this.buildInsertQuery(r)
        console.log('Insert query:', query)
        const resp = await axios.post(url, { query })
        if (!resp.data) throw new Error('No response')
        this.$bvToast && this.$bvToast.toast('Đã thêm bản ghi', { title: 'Upload', variant: 'success', autoHideDelay: 1500 })
        this.$set(this.previewRows[this.currentIndex], 'include', false)
        this.nextRecord()
      } catch (e) {
        console.error('Submit error:', e)
        alert('Lỗi khi thêm bản ghi: ' + e)
      }
    },
    onGenericSubmitted (id, isNewRecord) {
      // Called when GenericFormView successfully submits
      this.$bvToast && this.$bvToast.toast('Đã thêm bản ghi', { title: 'Upload', variant: 'success', autoHideDelay: 1500 })
      this.$set(this.previewRows[this.currentIndex], 'include', false)
      this.nextRecord()
    },
    async performImport () {
      if (this.isDryRun) {
        const inserted = this.previewRows.filter(r => r.include && !r.isDuplicate).length
        const skipped = this.previewRows.filter(r => !r.include || r.isDuplicate).length
        this.importSummary = { inserted, skipped, errors: [] }
        return
      }
      this.isImporting = true
      const rowsToInsert = this.previewRows.filter(r => r.include && !r.isDuplicate)
      let inserted = 0
      let skipped = this.previewRows.length - rowsToInsert.length
      let errors = []
      for (let i = 0; i < rowsToInsert.length; i++) {
        const row = rowsToInsert[i]
        try {
          const { query, url } = this.buildInsertQuery(row)
          const resp = await axios.post(url, { query })
          if (!resp.data) throw new Error('No response')
          inserted += 1
        } catch (e) {
          errors.push({ index: i, message: e.toString(), row })
        }
      }
      this.importSummary = { inserted, skipped, errors }
      this.isImporting = false
      this.$emit('imported', { target: this.localTarget, summary: this.importSummary })
    },
    buildInsertQuery (row) {
      const table = 'realestate.online_scan'
      const columns = []
      const values = []
      const add = (col, val, type = 'text') => {
        if (val === '' || val === null || val === undefined) return
        columns.push(col)
        if (type === 'number' || type === 'boolean') values.push(val)
        else values.push(`'${this.escapeSql(val)}'`)
      }
      // Online scan fields
      add('date', row.date)
      add('fullname', row.fullname)
      add('type', row.type)
      add('ward', row.ward)
      add('street', row.street)
      add('lineType', row.lineType)
      add('address', row.address)
      add('area', row.area, 'number')
      add('areaWidth', row.areaWidth, 'number')
      add('areaLength', row.areaLength, 'number')
      add('quantity', row.quantity, 'number')
      add('usageType', row.usageType)
      add('houseUsageArea', row.houseUsageArea, 'number')
      add('direction', row.direction)
      add('structure', row.structure)
      add('price', row.price, 'number')
      add('pricePerArea', row.pricePerArea, 'number')
      add('trait', row.trait)
      add('phone', row.phone)
      add('rent', row.rent, 'boolean')
      add('positive', row.positive)
      
      const columnsList = columns.join(',')
      const valuesList = values.join(',')
      const query = `INSERT INTO ${table} (${columnsList}) VALUES (${valuesList});`
      const url = Constants.HOST_URL + 'runQuery/insert'
      return { query, url }
    },
    escapeSql (val) {
      return (val || '').toString().replace(/'/g, "''")
    },
    chunkArray (arr, size) {
      const out = []
      for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size))
      return out
    },
    downloadErrors () {
      const lines = ['index,message']
      this.importSummary.errors.forEach(e => {
        lines.push(`${e.index},"${(e.message || '').replace(/"/g, '""')}"`)
      })
      const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'import_errors.csv'
      a.click()
      URL.revokeObjectURL(url)
    }
  }
}
</script>

<style scoped>
.mapping-grid { display: grid; grid-template-columns: 220px 1fr; grid-row-gap: 10px; grid-column-gap: 10px; }
.mapping-col-name { font-weight: 600; padding-top: 6px; }
</style>


